import type { HTMLProps, ReactElement } from 'react';
import { forwardRef } from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import Loader from '@app/components/Loader';
import { rem } from '@app/styles/theme/layout';

import {
  Container,
  giftFieldStyles,
  invalidStyles,
  Label,
} from './common.styles';

const Input = styled.textarea`
  ${giftFieldStyles}
  ${invalidStyles}
  ::placeholder {
    font-style: italic;
  }
`;

const InputLoader = styled(Loader)`
  position: absolute;
  top: ${rem(20)};
  right: ${rem(18)};
`;

interface TextAreaProps extends Omit<HTMLProps<HTMLTextAreaElement>, 'as'> {
  isValid?: boolean;
  formLabel?: string | ReactElement;
  half?: boolean;
  placeholder?: string;
  loading?: boolean;
  labelProps?: Omit<HTMLProps<HTMLLabelElement>, 'as'>;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    { formLabel, isValid, half, placeholder, loading, labelProps, ...props },
    ref,
  ) => {
    const theme = useTheme();

    return (
      <Container half={half}>
        <Input
          /*
           * isValid is an optional prop, so we check specifically against boolean
           */
          aria-invalid={isValid === false ? 'true' : 'false'}
          type="text"
          id={props.name}
          {...props}
          ref={ref}
          placeholder={placeholder ?? ' '}
          style={{ height: '100px', ...props.style }}
        />
        <Label {...labelProps} htmlFor={props.id || props.name}>
          {formLabel}
        </Label>
        {loading && <InputLoader color={theme.colors.labelGray} />}
      </Container>
    );
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
